import { Component, OnInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SkillprintSummaryService, SkillprintSummaryResponse } from '@app/services/api/skillprint-summary/skillprint-summary.service';
import { ConfigService } from '@app/services/config-service';
import { SurveysService } from '@app/services/api/surveys/surveys.service';
import { AuthService } from '@app/services/auth/auth.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ScoresComponent } from '@app/shared/survey/scores/scores.component';
import { AddResumeDialogComponent, AddResumeDialogResult } from '@app/shared/results/add-resume-dialog/add-resume-dialog.component';
import { environment } from '@environments/environment';
import { finalize } from 'rxjs';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { analytics } from '@app/events/segment/analytics';

interface SkillBarData {
  name: string;
  score: number;
  trait: string;
  description: string;
}

const SKILL_CATEGORY_MAPPING: Record<string, {
  scoreBandLabels: Record<number, string>,
  descriptions: Record<number, string>
}> = {
  'Innovative Thinking': {
    scoreBandLabels: {
      1: 'Conventional',
      2: 'Curious',
      3: 'Creative',
      4: 'Visionary',
    },
    descriptions: {
      1: "You're a conventional thinker, so we recommend upskilling in this area to boost your innovative thinking abilities.",
      2: "You're a curious thinker. You've already got some skills to help you think innovatively, but there is room for improvement!",
      3: "You're a creative thinker. You've got some great skills here, and with a little upskilling, you'll be a pro!",
      4: "You're a visionary--you excel at innovative thinking! We recommend focusing on other areas for improvement!",
    },
  },
  'Teamwork': {
    scoreBandLabels: {
      1: 'Independent',
      2: 'Selective',
      3: 'Collaborative',
      4: 'Synergistic',
    },
    descriptions: {
      1: "You're an independent contributor. We recommend upskilling in this area to become better at teamwork.",
      2: "You're selective about when and how you work on a team. You've already got some skills in this area, but there is room for improvement!",
      3: "You're collaborative. You've got some great skills here!",
      4: "You bring synergy to whatever team you're on. We recommend focusing on other areas for improvement!",
    },
  },
  'Diligence': {
    scoreBandLabels: {
      1: 'Casual',
      2: 'Committed',
      3: 'Reliable',
      4: 'Driven',
    },
    descriptions: {
      1: "You're more casual when it comes to your work. Upskilling can help you develop skills to diligently handle any work that comes your way.",
      2: "You're committed to your work. You've already got some skills in this area, but there is room for improvement!",
      3: "You're reliable at work. You've got some great skills here!",
      4: "You're driven to succeed. We recommend focusing on other areas for improvement!",
    },
  },
  'Stress Tolerance': {
    scoreBandLabels: {
      1: 'Receptive',
      2: 'Resilient',
      3: 'Composed',
      4: 'Thriving',
    },
    descriptions: {
      1: "You're receptive to stressful situations, even though they're difficult. We recommend upskilling in this area so you can always stay calm and collected.",
      2: "You're resilient to stress. You've already got some skills in this area, but there is room for improvement!",
      3: "You stay composed, despite the difficulties thrown your way. You've got some great skills here! Keep upskilling to excel.",
      4: "You thrive despite stressful situations, so we recommend focusing on other areas for improvement!",
    },
  },
  'Communication': {
    scoreBandLabels: {
      1: 'Clear',
      2: 'Articulate',
      3: 'Engaging',
      4: 'Influential',
    },
    descriptions: {
      1: "You're a clear communicator. We recommend upskilling in this area to become better at engaging and influencing others.",
      2: "You're articulate. You've already got some communication skills, but there is room for improvement!",
      3: "You're an engaging communicator, and people like to hear what you've got to say. You've got some great skills here!",
      4: "You're an influential communicator! You effectively and clearly communicate with others. We recommend focusing on other areas for improvement.",
    },
  },
};

@Component({
  selector: 'app-skillprint',
  standalone: true,
  imports: [CommonModule, MatDialogModule, NgxSpinnerModule],
  templateUrl: './skillprint.component.html',
  styleUrl: './skillprint.component.css'
})
export class SkillprintComponent implements OnInit {
  @Output() resumeUploaded = new EventEmitter<void>();
  skillprintSummary: SkillprintSummaryResponse | null = null;
  isLoading = false;
  loadingMessage = '';
  error: string | null = null;
  showAllSkills = false;
  skillsToShowInitially = 12; // Number of skills to show in collapsed state (approx 2 rows)
  public edgeCommunicationAssessmentUrl: string;
  public edgeBehavioralAssessmentUrl: string;
  private currentlyHoveredSkill: string | null = null;
  private _durableSkillBars: SkillBarData[] = [];

  constructor(
    private skillprintSummaryService: SkillprintSummaryService,
    private configService: ConfigService,
    private surveyService: SurveysService,
    private authService: AuthService,
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) {
    const tenantId = this.authService.getCurrentUser()?.tenantId;
    this.edgeCommunicationAssessmentUrl = this.configService.getCommunicationAssessmentUrl(tenantId);
    this.edgeBehavioralAssessmentUrl = this.configService.getBehavioralAssessmentUrl(tenantId);
  }

  ngOnInit(): void {
    this.loadSkillprintSummary();
  }

  loadSkillprintSummary(): void {
    this.isLoading = true;
    this.cdr.detectChanges();
    this.loadingMessage = 'Loading your skillprint summary…';
    this.error = null;

    this.skillprintSummaryService.getSkillprintSummary().subscribe({
      next: (response) => {
        this.skillprintSummary = response;
        this._durableSkillBars = this.computeDurableSkillBars(); // Cache computed skill bars
        this.isLoading = false;
        this.loadingMessage = 'Skillprint summary loaded.';

        // Track that the Understand Your Strengths section content has loaded
        analytics.track('Plan Strengths Available', {
          section: 'plan',
          scores: this._durableSkillBars,
          strengths: response.strengths?.map(strength => strength.title) || [],
          skills: response.skills || [],
        });
      },
      error: (error) => {
        console.error('Error loading skillprint summary:', error);
        this.error = 'Failed to load your skillprint summary. Please try again later.';
        this.isLoading = false;
        this.loadingMessage = 'Skillprint summary failed to load.';
      }
    });
  }

  get visibleSkills(): string[] {
    if (!this.skillprintSummary?.skills) return [];
    if (this.showAllSkills) return this.skillprintSummary.skills;
    return this.skillprintSummary.skills.slice(0, this.skillsToShowInitially);
  }

  get hasMoreSkills(): boolean {
    return (this.skillprintSummary?.skills?.length || 0) > this.skillsToShowInitially;
  }

  get remainingSkillsCount(): number {
    const totalSkills = this.skillprintSummary?.skills?.length || 0;
    return Math.max(0, totalSkills - this.skillsToShowInitially);
  }

  toggleShowSkills(): void {
    this.showAllSkills = !this.showAllSkills;
  }

  showSurveyResults(): void {
    analytics.track('Plan View Survey Insights', { section: 'plan' });
    this.spinner.show();
    this.loadingMessage = 'Loading survey results…';
    this.surveyService.getUserRiasecScore(environment.careerSurveyUuid).pipe(
      finalize(() => {
        this.spinner.hide();
        this.loadingMessage = 'Survey results loaded.';
      })
    ).subscribe({
      next: (response) => this.showRiasecScore(response),
      error: (error) => {
        console.log('Error fetching survey results:', error);
        this.error = 'Error while fetching survey results!';
      }
    });
  }

  showRiasecScore(data: any): void {
    if (data.riasec_score) {
      this.dialog.open(ScoresComponent, {
        data: {
          riasec: data.riasec_score,
          source: 'buildPlan'
        },
        disableClose: false,
      });
    }
  }

  openCommunicationAssessment(): void {
    analytics.track('Plan View Communication Insights', { section: 'plan' });
    const tenantId = this.authService.getCurrentUser()?.tenantId;
    const url = this.configService.getCommunicationAssessmentUrl(tenantId);
    window.open(url, '_blank');
  }

  openBehavioralAssessment(): void {
    analytics.track('Plan View Durable Insights', { section: 'plan' });
    const tenantId = this.authService.getCurrentUser()?.tenantId;
    const url = this.configService.getBehavioralAssessmentUrl(tenantId);
    window.open(url, '_blank');
  }

  openResumeDialog(): void {
    analytics.track('Plan View Resume', { section: 'plan' });
    const dialogRef = this.dialog.open(AddResumeDialogComponent, {
      panelClass: 'add-resume-dialog',
      width: '95%',
      maxWidth: '1632px',
      maxHeight: '95vh',
      data: { mode: 'view', redirectTo: '/build-plan', section: 'plan' },
    });

    dialogRef.afterClosed().subscribe((result?: AddResumeDialogResult) => {
      if (result?.resumeUploaded) {
        this.resumeUploaded.emit();
      }
    });
  }

  get durableSkillBars(): SkillBarData[] {
    return this._durableSkillBars;
  }

  private computeDurableSkillBars(): SkillBarData[] {
    const skillBarsMap = new Map<string, SkillBarData>();

    // Process all edge events to collect all categories
    if (this.skillprintSummary?.edgeEvents) {
      for (const edgeEvent of this.skillprintSummary.edgeEvents) {
        const assessmentType = edgeEvent.payload?.assessmentType;

        if (assessmentType === 'communication' && edgeEvent.payload?.scores?.communicationStyle) {
          // Handle communication assessment
          const commStyle = edgeEvent.payload.scores.communicationStyle;
          if (!skillBarsMap.has('Communication')) {
            skillBarsMap.set('Communication', {
              name: 'Communication',
              score: commStyle.overallScore,
              trait: commStyle.overallPerformance,
              description: commStyle.description || 'Your communication style assessment'
            });
          }
        } else if (assessmentType === 'behavioral' && Array.isArray(edgeEvent.payload?.scores)) {
          // Handle behavioral assessment with array of scores
          const scores = edgeEvent.payload.scores;

          for (const scoreItem of scores) {
            const category = scoreItem.category;
            const score = scoreItem.score;
            const trait = scoreItem.trait;
            const description = scoreItem.description;

            // Only add if we have the category in our mapping and haven't added it yet
            if (category && SKILL_CATEGORY_MAPPING[category] && !skillBarsMap.has(category)) {
              skillBarsMap.set(category, {
                name: category,
                score: score,
                trait: trait,
                description: description || `Your ${category.toLowerCase()} assessment results`
              });
            }
          }
        }
      }
    }

    // Define the desired order of categories
    const categoryOrder = ['Communication', 'Diligence', 'Stress Tolerance', 'Teamwork', 'Innovative Thinking'];

    // Return skills in the defined order, only including those that exist in the data
    const orderedSkills: SkillBarData[] = [];
    for (const category of categoryOrder) {
      if (skillBarsMap.has(category)) {
        orderedSkills.push(skillBarsMap.get(category)!);
      }
    }

    return orderedSkills;
  }

  getSegmentClass(skillBar: SkillBarData, segmentIndex: number): string {
    // segmentIndex is 0-based, score is 1-4
    // Only the segment at the score position gets the 'active' class (black)
    // All other segments remain teal (default background)
    if (segmentIndex + 1 === skillBar.score) {
      return 'active';
    }
    return '';
  }

  getSkillLabel(skillBar: SkillBarData): string {
    const mapping = SKILL_CATEGORY_MAPPING[skillBar.name];
    if (mapping && mapping.scoreBandLabels[skillBar.score]) {
      return mapping.scoreBandLabels[skillBar.score];
    }
    return skillBar.trait;
  }

  getSkillDescription(skillBar: SkillBarData): string {
    const mapping = SKILL_CATEGORY_MAPPING[skillBar.name];
    if (mapping && mapping.descriptions && mapping.descriptions[skillBar.score]) {
      return mapping.descriptions[skillBar.score];
    }
    return skillBar.description || 'Skill assessment information';
  }

  get formattedResumeUploadDate(): string {
    if (!this.skillprintSummary?.resumeUploadedAt) {
      return '';
    }

    const date = new Date(this.skillprintSummary.resumeUploadedAt);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    return date.toLocaleDateString('en-US', options);
  }

  onDurableSkillInfoHover(skillBar: SkillBarData): void {
    // Prevent multiple track events during the same hover session
    if (this.currentlyHoveredSkill === skillBar.name) {
      return;
    }
    this.currentlyHoveredSkill = skillBar.name;

    const assessmentType = skillBar.name === 'Communication' ? 'communication' : 'behavioral';
    const scoreBandLabel = this.getSkillLabel(skillBar);
    analytics.track('Plan Scores Info Open', {
      section: 'plan',
      assessment: assessmentType,
      score_category: skillBar.name,
      score_band: scoreBandLabel
    });
  }

  onDurableSkillInfoLeave(): void {
    this.currentlyHoveredSkill = null;
  }
}
